import { skipToken } from '@reduxjs/toolkit/query';
import { Navigate, useParams } from 'react-router';
import { selectOptionalUser } from 'features/auth/store/authSelectors';
import { protocolIdFromParticipationId } from 'features/brand/protocolIdFromPath';
import { useParticipationBrand } from 'features/brand/useParticipationBrand';
import { useProtocolStylesheet } from 'features/brand/useProtocolStylesheet';
import { useRequireAssetNamespace } from 'features/i18n/DynamicTranslation/useRequireAssetNamespace';
import { ParticipationShell } from 'features/layout/ParticipationShell';
import { useReportLoading } from 'features/loading';
import { logger } from 'features/logger/logger';
import { isActiveParticipation } from 'features/participations/logic/participationStatus';
import { useParticipationSubscriptionQuery } from 'features/participations/store/participationDocumentApiSlice';
import {
  selectOptionalParticipationDocData,
  selectParticipationDocumentNotFound,
} from 'features/participations/store/participationSelectors';
import { RoutePath } from 'features/routing/constants';
import { useRouteMatch } from 'features/routing/useRouteMatch';
import { useAppSelector } from 'features/store/hooks';
import { EmptyStatePage } from 'shared/components/EmptyStatePage';
import { useIsRouteAvailableWhenNotActive } from './useIsRouteAvailableWhenNotActive';
import { useSetUrlLocale } from './useSetUrlLocale';

type ProtectedParticipationRoutesProps = {
  withLayout?: boolean;
  // Renders the child route for any participation status; otherwise a
  // participation that is not active only reaches routesAvailableWhenNotActive.
  allowWhenNotActive?: boolean;
};

export const ProtectedParticipationRoutes = ({
  withLayout = true,
  allowWhenNotActive = false,
}: ProtectedParticipationRoutesProps) => {
  const { participationId } = useParams<{ participationId: string }>();
  const user = useAppSelector(selectOptionalUser);
  const participation = useAppSelector(selectOptionalParticipationDocData);
  const isAvailableWhenNotActive = useIsRouteAvailableWhenNotActive();
  const participationNotFound = useAppSelector(
    selectParticipationDocumentNotFound
  );

  const routeMatchStandalone = useRouteMatch([
    RoutePath.ParticipationTask(),
    RoutePath.ProtocolList,
    RoutePath.ProtocolEdit(),
    RoutePath.NotificationTemplatesMatrix(),
    RoutePath.NotificationTemplateEditor(),
  ]);

  const currentPathIsStandalone = !!routeMatchStandalone?.pattern.path;

  const currentPathIsHome = useRouteMatch([RoutePath.Home()]) !== null;
  const currentPathIsTask =
    useRouteMatch([RoutePath.ParticipationTask()]) !== null;

  const localeInitialized = useSetUrlLocale();

  useParticipationSubscriptionQuery(
    user && participationId ? { userId: user.uid, participationId } : skipToken
  );

  // Load the active participation's asset translation namespace for every
  // participation route, so a cold deep-link to a task page renders translated.
  useRequireAssetNamespace(participation?.assetId);

  // The Outlet renders only while the global participation slice matches the
  // route's participationId: lifecycle thunks key writes off this single-slot
  // slice but activityName off the route, so both must observe one participation.
  const participationMatchesRoute = participation?.id === participationId;

  const brandSettled = useParticipationBrand(
    protocolIdFromParticipationId(participationId),
    participationMatchesRoute
      ? (participation?.manifest.brand ?? null)
      : undefined
  );

  // The protocol stylesheet downloads with the brand on every participation
  // page; only task pages apply it and wait for it.
  const stylesheetSettled = useProtocolStylesheet(
    participationMatchesRoute ? participation?.manifest.style : undefined,
    currentPathIsTask
  );
  const stylesheetReady = stylesheetSettled || !currentPathIsTask;

  const isLoading =
    !participationNotFound &&
    (!user ||
      !participation ||
      !participationMatchesRoute ||
      !localeInitialized ||
      !brandSettled ||
      !stylesheetReady);
  useReportLoading(isLoading);

  if (!user) {
    logger.debug(`ProtectedParticipationRoutes wrapper, user is not signed in`);
    return null;
  }

  if (
    !participation ||
    !participationMatchesRoute ||
    !localeInitialized ||
    !brandSettled ||
    !stylesheetReady
  ) {
    if (participationNotFound) {
      return <EmptyStatePage />;
    }
    return null;
  }

  if (
    !isActiveParticipation(participation) &&
    !isAvailableWhenNotActive &&
    !allowWhenNotActive
  ) {
    return <Navigate to={RoutePath.StudyChoice} replace />;
  }

  return (
    <ParticipationShell
      participation={participation}
      withLayout={withLayout}
      standalone={currentPathIsStandalone}
      isHomeRoute={currentPathIsHome}
      userId={user.uid}
    />
  );
};
